import {
	getAboveTheFoldDirections,
	getAboveTheFoldPhone,
	getClickedImageAsset,
	getComponentLocation,
	getComponentType,
	getGalleryAsset,
	getGranularClickToCallAttributes,
	getSocialNetwork,
	getTotalAIVehicleImageCount,
	getTotalInventoryCTACount,
	getVDPDefaultVehicleImageAI,
	getVehicleDataForAttributes,
	isAdobeUpdatesOn,
	toAdobeLinkNameSlug,
	toSlug
} from '../../../utils/stellantis/utils';
import {
	GALLERY_WIDGETS,
	getGalleryInteraction
} from './utils/galleryInteraction';
import executeObjectFunctions from '../../../utils/executeObjectFunctions';

const isMobile = () => {
	return window?.DDC?.dataLayer?.page?.attributes?.layoutType === 'mobile';
};

// ── US2093316: legacy (pre-WSP-2.0) tagging teardown ────────────────────────
// Publicis confirmed the WSP 2.0 events REPLACE the legacy ones rather than
// running alongside them, so with ws-tagging-stellantis-adobe-updates ON the
// pre-2.0 payloads must stop. Flag OFF must remain byte-identical to today, so
// no legacy rule BODY and no legacy MATCHER is rewritten below. That matters:
// the legacy object-form logic is substring-based, not equality-based —
// `{ widgetName: 'model-selector' }` also matches 'model-selector-responsive'
// and `{ result: 'Scrolled to' }` matches any result containing it — so
// re-expressing those as `===` would silently change flag-off behavior.
//
// Two mechanisms, both already precedented in this codebase:
//
//   1. SUPPRESS_LEGACY — matches when the flag is on and carries no payload.
//      dataMapper sends nothing for a matched rule without one (its
//      `if (rule && rule.payload)` guard), and rule evaluation is
//      first-match-wins, so one of these placed AFTER the granular rules and
//      BEFORE the legacy tail switches the whole tail off. Used wherever an
//      event array is already partitioned that way. Precedent: the VDP gallery
//      image suppressor further down this file.
//   2. legacyPayload() — wraps a payload so it resolves to undefined when the
//      flag is on. dataMapper re-checks rule.payload AFTER executing it
//      ("since rule.payload may change, we need to check again"), so this also
//      sends nothing. Used for legacy rules sitting INTERLEAVED between
//      granular ones, where a suppressor would take the granular rules down
//      with them. Precedent: shiftnative's ddc.form.submission rule.
//
// The split applied throughout: turn a legacy rule OFF only where a granular
// rule demonstrably covers the SAME surface. Wherever WSP 2.0 defines an event
// but no granular rule reaches that surface yet, the legacy beacon deliberately
// KEEPS FIRING -- silencing it would lose the measurement rather than restate
// it. Those rules are individually commented "STILL FIRING", and the full list,
// the client questions and the eVar mappings are in
// docs/stellantis-wsp2-legacy-teardown.md.
const SUPPRESS_LEGACY = {
	logic: isAdobeUpdatesOn,
	payload: undefined
};

// executeObjectFunctions does NOT recurse into the RESULT of a top-level
// payload function, so resolve the wrapped payload here. Without this a legacy
// payload holding a NESTED function — the ws-model-selector 'Scrolled to'
// linkText is the one such case — would reach the sender unexecuted.
const legacyPayload = (payload) => (args) =>
	isAdobeUpdatesOn(args) ? undefined : executeObjectFunctions(payload, args);

// DDC's region string (eventData.location) -> SDG component type, per the
// US1906349 widget->type table's region rows. Used where the firing widget is
// not in COMPONENT_TYPE_BY_WIDGET, so `type` still lands on a real SDG
// component instead of being dropped. Same mapping the getDirections rule
// applies inline.
const REGION_COMPONENT_TYPE = {
	header: 'header',
	mainNav: 'header',
	footer: 'footer'
};

// NO LENGTH CAP on the error message, by decision (2026-09-14).
//
// SDG 4.18 asks for a "shortened version if error message is longer than 50
// characters" and that was originally applied, but it is incompatible with the
// client's requirement that EVERY mandatory field appear: the live five-field
// blank submit joins to 110 characters, so capping at 50 kept only two of the
// five fields and cut the third mid-word. A 255 ceiling was then tried to match
// Adobe's own limit, and that was dropped too — the message is now sent whole.
//
// What that means in practice: Adobe truncates eVars at 255 BYTES server-side
// ("values longer than 255 bytes are automatically truncated when sent to
// Adobe" — https://experienceleague.adobe.com/en/docs/analytics/implementation/vars/page-vars/evar),
// so a message past 255 is still cut, just by Adobe rather than here. Nothing in
// this repo can prevent that; removing our cap only removes the second, earlier
// truncation. Longest realistic value measured across Stellantis forms is ~407
// characters (a 16-field credit application); BusinessLink itself peaks at ~181.
//
// Note bytes != characters if a message ever carries non-ASCII. Today the slug
// output is [a-z0-9;-] only, so the two are equivalent.
//
// This supersedes the open Q7 in docs/stellantis-wsp2-legacy-teardown.md
// ("is a hard 50-character truncation correct, or would you rather have a short
// code?"). Reversible: reinstate a cap here if the client asks.
// ONE beacon carrying EVERY mandatory field's error message,
// semicolon-delimited — the client's requirement, e.g.
//   form-validation:first-name-is-required;last-name-is-required;email-is-required
//
// The sentences are what DDC actually publishes in eventData.invalidFields, and
// they already name their own field, so no copy is synthesised. Captured live on
// the BusinessLink form (note the trailing space and period, which the slug
// drops):
//   first_name:     "First Name is required. "     -> first-name-is-required
//   last_name:      "Last Name is required. "      -> last-name-is-required
//   email:          "Email is required. "          -> email-is-required
//   zip_code:       "Zip Code is required. "       -> zip-code-is-required
//   select_a_model: "Select a model is required. " -> select-a-model-is-required
//
// Semicolon by client request (2026-09-14). Safe as a delimiter because
// toErrorSlug collapses every non-alphanumeric run to '-', so a ';' can never
// appear inside a segment and the value always splits back cleanly. It is also
// one of the delimiters Adobe accepts for a list variable, so eVar104 can still
// be configured to count each field separately from the single beacon. Note
// event67 counts ONE site error per blocked submit, not one per field.
//
// Falls back to the field name when a field reports no message, so a
// message-less field is still named rather than silently dropped — the previous
// build only fell back when EVERY field was message-less, so a mixed set lost
// the message-less ones entirely.
const toErrorSlug = (value = '') =>
	String(value)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

const buildErrorMessage = (invalidFields = {}) =>
	Object.entries(invalidFields)
		.map(([field, message]) => toErrorSlug(message) || toErrorSlug(field))
		.filter(Boolean)
		.join(';');

// US2093316 (SDG 4.13 / 5.4.2.7) — Social Interactions. Shared by both social
// emits so their shape cannot drift. `description` is the network slug, resolved
// by the shared getSocialNetwork; 'n/a' is the right title for an unheaded icon
// row. NO linkUrl: unlike the CTA and Link Interaction events, the SDG's social
// payload and its variable table (event30 + event70 + eVar75) define none.
//
// `fixedType` is for ddc.link.socialHeaderClick: the header icon row sits INSIDE
// the contact-info widget, which the US1906349 widget->type table maps to
// `dealer-information` — but the SDG defines that component as dealer
// information in the BODY of the page, so deriving type from the widget is wrong
// there. The producer has already told us the region, so it is passed fixed (the
// granular click-to-call rule does the same for a phone in that same widget).
// ddc.link.socialShare has no such guarantee, so it still derives, with the DDC
// region taking precedence over the widget table.
const socialPayload =
	({ fixedType, defaultType } = {}) =>
	({ element, eventData = {} } = {}) => {
		const { widgetHeading, location } = eventData;
		const type =
			fixedType ||
			REGION_COMPONENT_TYPE[location] ||
			getComponentType({ eventData }) ||
			defaultType;

		return {
			type: 'CustomTagEvent',
			eventName: 'social',
			eventAction: 'pageElementInteraction',
			attributes: {
				site: 'dealer',
				type,
				location: getComponentLocation({ eventData, type }) || 1,
				title: widgetHeading
					? toAdobeLinkNameSlug(widgetHeading)
					: 'n/a',
				description: toAdobeLinkNameSlug(
					getSocialNetwork({ element, eventData })
				)
			}
		};
	};

// eVar75 is pipe-delimited, so a literal "|" in a value injects extra segments.
// Guard only, not a slug: a value with no pipe is returned unchanged.
const collapsePipes = (value) =>
	typeof value === 'string' ? value.replace(/\s*\|\s*/g, ' ').trim() : value;

// US2046693: the <component title> for an inventory-card interaction is the
// vehicle, slugged — the SDG examples read
// `dealer|vehicle-inventory|1|2026-chrysler-pacifica-select-awd|pricing`. The
// data layer publishes `year` on a click vehicle and `modelYear` on the
// page-level vehicles list, so accept either.
const getVehicleTitle = ({ year, modelYear, make, model, trim } = {}) =>
	toSlug([year || modelYear, make, model, trim].filter(Boolean).join(' ')) ||
	'n/a';

// US2046691: the gallery's non-media controls. SDG 5.4.2.8 (Next/Previous) and
// 5.4.2.10 (Overlay Close) both use the linkClick block; `description` is the
// only thing that varies. Close is typed `gallery` rather than `overlay` so it
// stays inside the Gallery Component taxonomy, whose own eVar75 examples list
// `dealer|gallery|1|n/a|close`.
//
// The zoom/fullscreen values are INVENTED: the client boxed those controls
// (feedback deck p.8, "Photo Gallery Controls") but the SDG defines no
// description for either, so these follow its hyphen-delimited <component text>
// convention (cf. the Navigation event's `<description>-open`/`-close`).
// Publicis must approve or replace them. They only ever fire on
// ws-vehicle-media — ws-vehicle-gallery's modal has no zoom or fullscreen.
const GALLERY_CONTROL_DESCRIPTIONS = {
	next: 'next',
	previous: 'previous',
	close: 'close',
	zoomIn: 'zoom-in',
	zoomOut: 'zoom-out',
	fullscreenEnter: 'fullscreen-enter',
	fullscreenExit: 'fullscreen-exit'
};

const getFormMapping = (mapping, trackingId) => {
	let entry = mapping[trackingId?.toUpperCase()];

	if (typeof entry?.type === 'undefined') {
		entry = mapping.OTHER;
	}

	const { lead, type, displayFormat, displayType } = entry;

	// displayFormat/displayType are optional per-form overrides; absent for
	// every entry but the ones whose story asks for the SDG spelling.
	return {
		formDescription: lead ? 'lead' : 'non-lead',
		formType: type,
		displayFormat,
		displayType
	};
};

const getFormAttrs = ({ isModal, mapping, trackingId, vehicle, vehicles }) => {
	const { formDescription, formType, displayFormat, displayType } =
		getFormMapping(mapping.form, trackingId);

	// A modal is a modal whatever the table says; the overrides only respell
	// the in-page case.
	return {
		displayFormat: isModal ? 'modal' : displayFormat ?? 'inPage',
		displayType: isModal ? 'modal' : displayType ?? 'in-page',
		formDescription,
		formType,
		...getVehicleDataForAttributes({
			eventData: {
				vehicle
			},
			vehicles
		})
	};
};

const getDRLinkText = (element, widgetState) => {
	const finString = 'digitalretailing:build my deal:finance';
	const leaseString = 'digitalretailing:build my deal:lease';
	let conditionalString;

	switch (element) {
		case 'Credit Tier':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:credit score`;
			} else {
				conditionalString = `${leaseString}:credit score`;
			}
			break;
		case 'Payment Term':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:financing`;
			} else {
				conditionalString = `${leaseString}:term`;
			}
			break;
		case 'Incentives':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:specials`;
			} else {
				conditionalString = `${leaseString}:specials`;
			}
			break;
		case 'Tradein':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:add a trade in`;
			} else {
				conditionalString = `${leaseString}:add a trade in`;
			}
			break;
		case 'Down Payment':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:cash down`;
			} else {
				conditionalString = `${leaseString}:cash down`;
			}
			break;
		case 'Offer Price':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:send my offer`;
			} else {
				conditionalString = `${leaseString}:send my offer`;
			}
			break;
		case 'Continue To Sdp':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:apply for credit`;
			} else {
				conditionalString = `${leaseString}:send my offer`;
			}
			break;
		case 'Lease Annual Miles':
			conditionalString = `${leaseString}:miles per year`;
			break;
		default:
		// do nothing
	}

	return conditionalString;
};

const formatUUID = (str) => {
	if (!str || str.length !== 32) {
		return str;
	}

	return `${str.slice(0, 8)}-${str.slice(8, 12)}-${str.slice(
		12,
		16
	)}-${str.slice(16, 20)}-${str.slice(20)}`;
};

// US2046697 — map interaction events (SDG 5.4.2.12). All map interactions
// (pin/zoom/pan) emit the same beacon; the client only needs to know an
// interaction occurred. Gated behind ws-tagging-stellantis-adobe-updates so
// the already-released pin beacon can fall through to its legacy payload when
// the flag is off. `title` is the closest section heading (ws-tracking
// pushEvent adds eventData.widgetHeading); it falls back to 'n/a' when there
// is no heading (US2061368 — was '' per the SDG; Stellantis may revise later).
//
// `location` is a hardcoded 1, NOT a counted ordinal (US2061364). The WSP 2.0
// SDG (4-14-2026) describes the Dealer Information Component as "dealer
// information in the body of the page, usually with a map for directions" and
// shows every one of its eVar75 examples at location 1 — including the map
// itself: `dealer|dealer-information|1|get-in-touch|map`. The map is an element
// INSIDE that single component, not a component of its own, so it must not be
// counted against the other dealer-information widgets on the page.
// Do not replace this with getComponentLocation: that counts DDC widgets
// (contact-info, map-dynamic, ws-hours are three) and yields 2 or 3.
const isFlaggedMapInteraction = ({ eventData, flags } = {}) =>
	isAdobeUpdatesOn({ flags }) && eventData?.widgetName === 'map-dynamic';

const mapInteractionPayload = {
	type: 'CustomTagEvent',
	eventName: 'map',
	eventAction: 'pageElementInteraction',
	attributes: {
		site: 'dealer',
		type: ({ eventData } = {}) => getComponentType({ eventData }),
		location: 1,
		description: 'map',
		title: ({ eventData } = {}) =>
			eventData?.widgetHeading
				? toAdobeLinkNameSlug(eventData.widgetHeading)
				: 'n/a'
	}
};

export default {
	events: {
		'ddc.wstagging.first': [
			{
				payload: {
					digitalData: {
						page: {
							pageInfo: ({ flags } = {}) => ({
								dealerID:
									'[[dealerCode.cllc]] >> [[dealerCode.ar]] >> [[dealerCode.fiat]]',
								dealerName: '[[dealershipName]]',
								geoRegion: '[[country]]',
								language: '[[lookup.locale]]',
								pageName: `dealer:${window?.DDC?.dataLayer?.dealership?.country?.toLowerCase?.()}:[[fn.buildPageName]]`,
								providerName: 'DealerDotCom',
								responsiveState: '[[layoutType]]',
								siteTechnologyVersion: '[[variationId]]',
								tier3_dealerState: '[[stateProvince]]',
								tier3_dealerZipcode: '[[postalCode]]',
								tier3_trafficType: '',
								websiteTier: '[[tier]]',
								// Adobe Launch reads eVar59 from zipcode
								zipcode: '[[userPostalCode]]',
								...(isAdobeUpdatesOn({ flags }) && {
									templateId: '[[pageAlias]]',
									totalAIVehicleImageCount:
										getTotalAIVehicleImageCount({
											vehicles:
												window?.DDC?.dataLayer?.vehicles
										}),
									// Scanned live at this ddc.wstagging.first moment
									// (US2041527) so the value reflects the current DOM —
									// the getters also publish onto DDC.dataLayer so it is
									// reusable and digitalData mirrors the data layer.
									aboveTheFoldPhone: getAboveTheFoldPhone(),
									aboveTheFoldDirections:
										getAboveTheFoldDirections()
								}),
								// US1906327: VDP-only per spec. Value is produced
								// by ddc-tracking-helpers at page load.
								...(isAdobeUpdatesOn({ flags }) &&
									window?.DDC?.dataLayer?.page?.pageInfo
										?.isVdp && {
										totalInventoryCTACount:
											getTotalInventoryCTACount()
									}),
								...(window?.DDC?.dataLayer?.page?.pageInfo
									?.isVdp && {
									vehicleMake: '[[make]]',
									vehicleModel: '[[model]]',
									vehicleStatus: '[[fn.getVehicleType]]',
									vehicleTrim: '[[trim]]',
									vehicleVin: '[[vin]]',
									vehicleYear: '[[modelYear]]',
									tier3_vehicleDisplayedPrice:
										'[[internetPrice]]',
									tier3_vehicleEngine: '[[engine]]',
									tier3_vehicleExteriorColor:
										'[[exteriorColor]]',
									tier3_vehicleInteriorColor:
										'[[interiorColor]]',
									tier3_vehicleMsrp: '[[internetPrice]]',
									tier3_vehicleStockNumber: '[[stockNumber]]',
									tier3_vehicleTransmission:
										'[[transmission]]',
									inventoryStatus: '[[fn.getInventoryStatus]]'
								}),
								...(isAdobeUpdatesOn({ flags }) && {
									vehicleCCcode:
										'[[fn.getStellantisVehicleCCcode]]'
								})
							}),
							category: {
								pageType: '[[fn.buildPageType]]'
							}
						},
						events: [],
						version: '1.0',
						// probably the debug is another gap we need to fulfill
						debug: false,
						user: {
							authenticationState: null
						}
					}
				}
			}
		],
		'ddc.page.view': [
			{
				// With the ws-tagging-stellantis-adobe-updates flag on,
				// inventory pages get their page view from the
				// inventoryPageView trigger on ddc.inventory.filterSearch,
				// so skip the standard page view when the pageAlias
				// includes 'INVENTORY'. With the flag off, keep the old
				// behavior and always fire the standard page view.
				logic: ({ flags } = {}) =>
					!isAdobeUpdatesOn({ flags }) ||
					!(window?.DDC?.dataLayer?.page?.pageInfo?.pageName || '')
						.toUpperCase()
						.includes('INVENTORY'),
				payload: {
					type: 'CustomTagEvent',
					eventName: 'JSpageView',
					eventAction: 'contentView',
					attributes: ({ flags } = {}) => ({
						pageID: '[[window.digitalData.page.pageInfo.pageName]]_virtualPageView',
						...(window?.DDC?.dataLayer?.page?.pageInfo?.isVdp &&
							isAdobeUpdatesOn({ flags }) && {
								VDPDefaultVehicleImageAI:
									getVDPDefaultVehicleImageAI({
										vehicles:
											window?.DDC?.dataLayer?.vehicles
									})
							})
					})
				}
			}
		],
		'ddc.page.vehicleDetailView': [
			// US2093316: the legacy detailsView/vehicle beacon. WSP 2.0 has no
			// equivalent event -- the VDP vehicle attributes are carried by the
			// page-load digitalData instead (SDG 5.3.1.1) -- so it stops here.
			SUPPRESS_LEGACY,
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'detailsView',
					eventAction: 'vehicle',
					attributes: {
						cCode: '[[fn.getVehicleCCcode]]',
						year: '[[year]]',
						make: '[[make]]',
						model: '[[model]]',
						trim: '[[trim]]',
						status: '[[fn.getVehicleType]]'
					}
				}
			}
		],
		'ddc.form.initiation': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'start',
					eventAction: 'form',
					attributes: ({
						eventData: { isModal, trackingId, vehicle = {} } = {},
						mapping
					} = {}) =>
						getFormAttrs({
							isModal,
							mapping,
							trackingId,
							vehicle,
							vehicles: window?.DDC?.dataLayer?.vehicles
						})
				}
			}
		],
		'ddc.form.confirmation': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'submit',
					eventAction: 'form',
					attributes: ({
						eventData: {
							fields,
							formEventId,
							isModal,
							submissionTimestamp,
							trackingId,
							vehicle = {}
						} = {},
						mapping
					} = {}) => ({
						...getFormAttrs({
							isModal,
							mapping,
							trackingId,
							vehicle,
							vehicles: window?.DDC?.dataLayer?.page?.pageInfo
								?.isConfirm
								? [vehicle]
								: window?.DDC?.dataLayer?.vehicles
						}),
						leadId: `${formatUUID(
							formEventId
						)}:${submissionTimestamp}`,
						hashedEmail: fields?.email
					})
				}
			}
		],
		'ddc.slideshow.click': [
			// US2093316 — STILL FIRING with the flag on, deliberately. WSP 2.0 covers carousels via
			// Next/Previous (5.4.2.8), Carousel Index Selection (5.4.2.9) and Media
			// Interaction (5.4.2.13), but none of those is built for the CMS
			// slideshow surface, so silencing it would lose the measurement
			// rather than restate it. See docs/stellantis-wsp2-legacy-teardown.md.
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'carousel',
						assetName: '[[eventData.name]]',
						assetPosition: '[[eventData.position]]'
					}
				}
			}
		],
		widgetUserEvent: [
			{
				// US2046701: sitewide overlay close for the MyCars nav, which
				// dismisses via a widgetUserEvent rather than ddc.dialog.closed.
				// Emits the same overlay-close CustomTagEvent; pageName is read
				// from the data layer since the widget event carries none.
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.widgetName === 'ws-mycars-nav' &&
					eventData?.result === 'MyCars menu closed',
				payload: ({ eventData } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'overlay',
						location: 1,
						description: 'close',
						title: eventData?.element
							? toAdobeLinkNameSlug(eventData.element)
							: 'n/a'
					}
				})
			},
			{
				logic: {
					'eventData.widgetName': 'ws-site-text-search',
					'eventData.element': 'search query typed in',
					'eventData.action': 'impression'
				},
				payload: ({ eventData }) => ({
					type: 'CustomTagEvent',
					eventName: 'internal',
					eventAction: 'search',
					attributes: {
						searchTerm: eventData.fieldValue,
						type: 'other',
						searchResultsNumber: eventData.pageSearchResultsCount
					}
				})
			},
			// US2050676: header search overlay CLOSE — SDG 5.4.2.3 Link
			// Interaction, Header taxonomy dealer|header|1|n/a|search-close.
			// The close button (data-testid flyout-close-button) fires ONLY
			// a widgetUserEvent from ws-site-text-search — no
			// ddc.link.generic — so this must match the WUE channel.
			// Numeric `location` is 1 per the header taxonomy (US2061364);
			// the close doesn't navigate, so no `linkUrl`. Gated behind
			// ws-tagging-stellantis-adobe-updates; when off, close stays
			// untagged (prior behavior).
			{
				logic: ({
					eventData: { widgetName, element, action } = {},
					flags
				} = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					widgetName === 'ws-site-text-search' &&
					element === 'close button' &&
					action === 'clicked',
				payload: {
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'header',
						location: 1,
						title: 'n/a',
						description: 'search-close'
					}
				}
			},
			// US2050676: search field CLEAR — SDG 5.4.1.4 Search Component,
			// taxonomy dealer|search|1|n/a|clear. The clear button
			// (data-testid clear-button) fires both a widgetUserEvent and a
			// ddc.link.generic; we match the WUE for consistency with close.
			// No `searchTerm` — the clear event carries none and the SDG
			// clear example shows none. Same flag gate and location/linkUrl
			// omissions as the close rule.
			{
				logic: ({
					eventData: { widgetName, element, action } = {},
					flags
				} = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					widgetName === 'ws-site-text-search' &&
					element === 'clear button' &&
					action === 'clicked',
				payload: {
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: ({ eventData } = {}) =>
							getComponentType({ eventData }),
						location: 1,
						title: 'n/a',
						description: 'clear'
					}
				}
			},
			// US2046696: "Get Directions" CTA button (contact page) — SDG
			// 5.4.2.6. A ws-content-cta button firing a widgetUserEvent; DDC
			// skips its #directions hash anchor so there is no ddc.link.generic
			// to match. Per the US1906349 widget->type table ws-content-cta is
			// `feature`. `location` is the widget's numeric position
			// (US2061364); description is the slugged CTA text
			// (get-directions). Gated behind
			// ws-tagging-stellantis-adobe-updates; off keeps it untagged.
			{
				logic: ({
					eventData: { widgetName, elementCTA, action } = {},
					flags
				} = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					widgetName === 'ws-content-cta' &&
					elementCTA === 'Get Directions' &&
					action === 'clicked',
				payload: ({ eventData = {} } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						site: 'dealer',
						type: getComponentType({ eventData }),
						location: getComponentLocation({ eventData }),
						title: eventData.widgetHeading
							? toAdobeLinkNameSlug(eventData.widgetHeading)
							: 'n/a',
						description: toAdobeLinkNameSlug(eventData.elementCTA)
					}
				})
			},
			{
				logic: {
					'eventData.action': 'clicked',
					'eventData.element': 'Save button',
					'eventData.result': 'Vehicle saved'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'save',
					eventAction: 'vehicle',
					attributes: '[[fn.getVehicleDataForAttributes]]'
				}
			},
			{
				logic: {
					'eventData.action': 'clicked',
					'eventData.result':
						'Email Signup for Price Alert Notification'
				},
				payload: legacyPayload({
					type: 'CustomTagEvent',
					eventName: 'priceAlert',
					eventAction: 'vehicle',
					attributes: '[[fn.getVehicleDataForAttributes]]'
				})
			},
			{
				logic: [
					// We want to only do specific DR events as not all of them are needed
					{
						widgetName: 'digital-retailing-cox',
						element: 'Credit Tier'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Payment Term'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Incentives'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Tradein'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Down Payment'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Offer Price'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Continue To Sdp'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Lease Annual Miles'
					}
				],
				payload: legacyPayload(
					({ eventData: { element, widgetState } }) => ({
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction:
							element === 'Tradein' ||
							element === 'Continue To Sdp'
								? 'buttonClick'
								: 'pageElementInteraction',
						attributes: {
							linkText: getDRLinkText(element, widgetState),
							isDigitalRetailing: true
						}
					})
				)
			},
			{
				logic: {
					widgetName: 'ws-media-viewer',
					'eventData.result': 'Opens gallery modal'
				},
				payload: ({ eventData: { index } }) => ({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: `in-page:gallery:${window.DDC.dataLayer.page.pageInfo.pageName
							.toLowerCase()
							.replace('sitebuilder_', '')
							.replace('_1', '')
							.replaceAll('_', '-')}-image-${index}-open`
					}
				})
			},
			{
				logic: {
					widgetName: 'ws-rec-vehicles',
					action: 'clicked'
				},
				// US2093316 — STILL FIRING with the flag on: WSP 2.0 defines an event
				// for this surface, but no granular rule covers it yet.
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText:
							'view featured inventory [[eventData.year]] [[eventData.make]] [[eventData.model]]'
					}
				}
			},
			{
				logic: {
					widgetName: 'ws-model-selector',
					result: 'Model selector filtered'
				},
				// US2093316 — STILL FIRING with the flag on: WSP 2.0 defines an event
				// for this surface, but no granular rule covers it yet.
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				}
			},
			{
				logic: {
					widgetName: 'ws-model-selector',
					result: 'Navigated to model listings'
				},
				// US2093316 — STILL FIRING with the flag on: WSP 2.0 defines an event
				// for this surface, but no granular rule covers it yet.
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				}
			},
			{
				logic: {
					widgetName: 'ws-model-selector',
					result: 'Scrolled to'
				},
				// US2093316 — STILL FIRING with the flag on: WSP 2.0 defines an event
				// for this surface, but no granular rule covers it yet.
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: ({ eventData }) =>
							`browse new models ${
								eventData?.elementCTA?.includes?.('Next')
									? 'next'
									: 'previous'
							}`
					}
				}
			},
			{
				logic: ({ eventData, flags }) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.widgetName === 'model-selector' &&
					eventData?.result === 'Model selector filtered',
				payload: ({ eventData } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						site: 'dealer',
						type: getComponentType({ eventData }),
						location: getComponentLocation({ eventData }),
						description: toAdobeLinkNameSlug(eventData?.elementCTA),
						// cms-web #6855 renamed this from headerText; fall back
						// to 'n/a' like every other title in this file so a
						// release-order skew can't ship an empty segment.
						title: eventData?.widgetHeading
							? toAdobeLinkNameSlug(eventData.widgetHeading)
							: 'n/a'
					}
				})
			},
			// US2046700 - Added a new event above this one that supersedes this and provides more information.
			// This event will need to be cleaned up after we fully enable the ws-tagging-stellantis-adobe-updates flag.
			{
				logic: {
					widgetName: 'model-selector',
					result: 'Model selector filtered'
				},
				payload: legacyPayload({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				})
			},
			{
				logic: {
					widgetName: 'model-selector',
					result: 'Navigated to model listings'
				},
				payload: legacyPayload({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				})
			},
			{
				logic: ({ eventData, flags }) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.widgetName === 'ws-mycars-nav' &&
					[
						'Recently viewed link',
						'Tracking price link',
						'Saved link'
					].includes(eventData?.element),
				payload: ({ eventData }) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'overlay',
						location: 1,
						// eventData.element in this case has a "-link" suffix that we don't want.
						description: toAdobeLinkNameSlug(
							eventData.element
						).replace(/-link$/, ''),
						title: 'shopping-activity',
						linkUrl: eventData.url
					}
				})
			},
			{
				logic: ({ eventData, flags }) =>
					isAdobeUpdatesOn({ flags }) &&
					(eventData?.widgetName === 'ws-detailed-pricing' ||
						eventData?.widgetName === 'ws-inv-listing') &&
					eventData?.element === 'Details button',
				payload: {
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'overlay',
						location: 1,
						description: 'details',
						title: 'n/a'
					}
				}
			},
			// ── US2046691: VDP media gallery (SDG 5.4.1.11 / 5.4.2.13) ──────────
			// Stellantis sites run one of two gallery widgets (ws-vehicle-media or
			// ws-vehicle-gallery, ~50/50, selected by the VDP page-layout id).
			// getGalleryInteraction normalizes their differing element/result
			// vocabularies, so these rules cover both. The legacy ws-media-viewer
			// rules above are the CMS *content* gallery — a different surface.
			// Numeric `location` is 1 (US2061364) — the gallery is a single
			// overlay surface, not a repeatable widget.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					getGalleryInteraction(eventData)?.kind === 'image',
				payload: ({ eventData } = {}) => {
					const { index } = getGalleryInteraction(eventData) || {};
					const { assetName, assetSource } = getGalleryAsset({
						index,
						vehicle: window?.DDC?.dataLayer?.vehicles?.[0]
					});

					return {
						type: 'CustomTagEvent',
						eventName: 'media',
						eventAction: 'interaction',
						attributes: {
							site: 'dealer',
							type: 'gallery',
							location: 1,
							title: 'n/a',
							description: 'click',
							assetType: 'image',
							assetSource,
							assetName
						}
					};
				}
			},
			// ── US2046691: VDP gallery controls (SDG 5.4.2.8 / 5.4.2.10) ────────
			// Next, previous, and close — covering both gallery widgets and, via
			// getGalleryInteraction's carouselDirection fallback, mobile swipe.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					Boolean(
						GALLERY_CONTROL_DESCRIPTIONS[
							getGalleryInteraction(eventData)?.kind
						]
					),
				payload: ({ eventData } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'gallery',
						location: 1,
						title: 'n/a',
						description:
							GALLERY_CONTROL_DESCRIPTIONS[
								getGalleryInteraction(eventData).kind
							]
					}
				})
			}
		],
		thirdPartyUserEvent: [
			// US2046690 (SDG 5.4.2.2) — the MyWallet links the Web Integration API
			// injects into the VDP `vehicle-payments` location ("Apply for
			// Financing" and "Personalize Payment"). They are NOT DDC markup, so
			// there is no widget to key on and their ddc.link.generic twin carries
			// only a generic `d-block` class; `product: 'mywallet'` plus
			// `step: 'Vehicle Payments'` identifies them unambiguously and lets one
			// rule cover both links, with `element` supplying the description.
			// Placed before the My Wallet modal rule below, which matches on
			// `element` alone and must keep its own overlay payload.
			// `type` is `vehicle-inventory` to match the other VDP CTAs; there is no
			// vehicle or heading on this event, so `title` is 'n/a'. Numeric
			// `location` deferred (US1906349).
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.product === 'mywallet' &&
					eventData?.step === 'Vehicle Payments',
				payload: ({ eventData = {} } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction:
						eventData?.element === 'Details'
							? 'linkClick'
							: 'buttonClick',
					attributes: {
						site: 'dealer',
						type:
							eventData?.element === 'Details'
								? 'overlay'
								: 'vehicle-inventory',
						location: 1,
						description: toSlug(eventData.element),
						title: 'n/a'
					}
				})
			}
		],
		'ddc.link.offerClick': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'offerClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						offerId: '[[eventData.offerId]]',
						offerName: '[[eventData.offerName]]'
					}
				}
			}
		],
		// US2046701: sitewide overlay close tracking. Adobe Launch maps this
		// CustomTagEvent to event70 (Link Click) / eVar75 (Link Name) as the
		// taxonomy dealer|overlay|<location>|<title>|<text>.
		// US2061368 QA: these titles shipped RAW ('Track Price', 'Hours',
		// 'MyCars nav'), which the WSP 2.0 SDG (4-14-2026) disallows twice over.
		// Its "Notes on Case Sensitivity" require every variable be passed lower
		// case except Tracking Code, eVar31, eVar51, eVar78 and eVar80 — eVar75 is
		// not exempt — and it defines <component title> as a "hyphen delimited
		// value where necessary". The SDG's own Overlay Close examples are
		// `dealer|overlay|2|my-profile|close` and `dealer|overlay|3|contact-us|close`.
		// Slug them like every other granular title so one Adobe dimension stops
		// carrying two formats, and so a dealer-authored heading containing a '|'
		// cannot inject an extra eVar75 segment (toAdobeLinkNameSlug collapses it).
		'ddc.dialog.closed': [
			{
				logic: ({ flags } = {}) => isAdobeUpdatesOn({ flags }),
				payload: ({ eventData } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'overlay',
						location: 1,
						description: 'close',
						title: eventData?.dialogTitle
							? toAdobeLinkNameSlug(eventData.dialogTitle)
							: 'n/a'
					}
				})
			}
		],
		'ddc.link.navClick': [
			// US2093316: the legacy navClick beacon. WSP 2.0 reports nav opens and
			// closes as linkClick (5.4.2.11), which the granular ddc.link.generic
			// rules already emit.
			SUPPRESS_LEGACY,
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.linkText]]',
						position: isMobile()
							? 'sidebar'
							: '[[eventData.position]]'
					}
				}
			}
		],
		// TA4137176 (SDG 5.4.2.11): a desktop megamenu closed WITHOUT clicking its
		// own toggle — clicking anywhere outside it, or ESC with the toggle focused
		// — fired no DDC event at all, so Adobe never received the close (the
		// dropdown rule below only sees the toggle's own ddc.link.generic).
		// ws-tracking now emits ddc.link.navToggle from bootstrap's
		// hide.bs.dropdown, which fires ONLY for those dismissals: never on a
		// toggle re-click, never when another menu is opened, never on open — so
		// this cannot double-count the close that already works. Same header
		// taxonomy as the toggle-click open/close, including `location: 1`
		// (US2061364).
		'ddc.link.navToggle': [
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.navToggleState === 'close',
				payload: ({ eventData: { text = '' } = {} }) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'header',
						location: 1,
						title: 'n/a',
						description: `${toAdobeLinkNameSlug(text)}-close`
					}
				})
			}
		],
		'ddc.inventory.filterSearch': [
			// US2093316: the legacy filtered/search beacon. WSP 2.0 defines only
			// the 'internal' search event (SDG 4.4), so this shape stops here.
			// The trigger MUST be repeated on this rule: dataMapper reads
			// rule.trigger from the matched rule regardless of whether it has a
			// payload, so this is what keeps the flag-on inventory page view
			// (triggerEvents.inventoryPageView) firing once the legacy rule below
			// is no longer reached.
			{
				logic: isAdobeUpdatesOn,
				payload: undefined,
				trigger: 'inventoryPageView'
			},
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'filtered',
					eventAction: 'search',
					attributes: {
						filters: '[[fn.buildFilteredSearchFilters]]',
						searchResultsNumber: '[[eventData.resultCount]]',
						type: '[[fn.getFilteredSearchType]]'
					}
				},
				// Also serve a page view whenever the pageAlias includes
				// 'INVENTORY' (see triggerEvents.inventoryPageView).
				trigger: 'inventoryPageView'
			}
		],
		'ddc.inventory.typedSearch': [
			// US2093316: the legacy typed/search beacon. WSP 2.0 defines only the
			// 'internal' search event (SDG 4.4), which the ws-site-text-search
			// widgetUserEvent rule already emits in the correct shape.
			SUPPRESS_LEGACY,
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'typed',
					eventAction: 'search',
					attributes: {
						searchTerm: '[[eventData.queryFacets.search]]',
						searchResultsNumber: '[[eventData.resultCount]]',
						type: '[[fn.getFilteredSearchType]]'
					}
				}
			}
		],
		'ddc.form.paymentCalcInit': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'start',
					eventAction: 'paymentCalculator',
					attributes: '[[fn.getVehicleDataForAttributes]]'
				}
			}
		],
		'ddc.link.generic': [
			// US2046696: Get Directions granular events (SDG 5.4.2.6). Every
			// directions link (url /dealership/directions.htm) — the header
			// address, nav (About > Directions), footer, and body dealer-info
			// block — fires ddc.link.generic. The hosting widget varies
			// (header-default / contact-info / ws-navigation), so we key on
			// the url and derive `type` from eventData.location, not
			// widgetName. Numeric `location` is hardcoded 1 (US2061364);
			// `description` is the slugged element text. type per
			// US1906349: header/mainNav -> header, footer -> footer, content ->
			// dealer-information.
			//
			// MUST STAY FIRST — not just for flag-off fall-through to the legacy
			// linkText getDirections rule below (no Adobe double-count), but
			// because the footer "Directions" link is a ws-navigation footer nav
			// link and so ALSO matches US2046694's footer rule further down.
			// first-match-wins: reorder these two and a footer directions click
			// silently becomes linkClick{type:footer}. Locked by the
			// "(order lock)" test in stellantis.test.js.
			//
			// The map's "Visit us at" link is an external Google URL, so it
			// never appears here; it is tagged separately in the
			// ddc.link.getDirections array below, off the ws-tracking emit
			// (US2046696 cross-repo: cms-web adds data-ws-tracking-directions,
			// ws-tracking matches it).
			// US2061368 QA: `title` here used to be a hardcoded 'n/a', which made a
			// Directions link the only granular event in a titled widget that threw
			// its heading away — in one footer links-list headed 'Our Dealership',
			// Contact Us reported title `our-dealership` (the footer linkClick rule
			// below) while Directions reported `n/a`. Slug the heading the same way
			// that rule does, and keep 'n/a' for the headingless widgets (header,
			// mainNav, map-dynamic) where it is still the right answer.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.url === '/dealership/directions.htm',
				payload: ({
					eventData: { location, text, widgetHeading } = {}
				}) => ({
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						site: 'dealer',
						type:
							{
								header: 'header',
								mainNav: 'header',
								footer: 'footer',
								content: 'dealer-information'
							}[location] || 'dealer-information',
						location: 1,
						title: widgetHeading
							? toAdobeLinkNameSlug(widgetHeading)
							: 'n/a',
						description: toAdobeLinkNameSlug(text)
					}
				})
			},
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					(eventData?.widgetName === 'navbar-default' ||
						eventData?.widgetName === 'ws-contact') &&
					eventData?.text === 'Get Directions',
				payload: ({ eventData = {} } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						site: 'dealer',
						type:
							eventData?.widgetName === 'navbar-default'
								? 'header'
								: 'sidebar',
						location: 1,
						title: 'n/a',
						description: toAdobeLinkNameSlug(eventData?.text)
					}
				})
			},
			// US2046694 (SDG 5.4.2.11) — net-new GRANULAR sidebar OPEN. The
			// mobile hamburger toggle (isHamburgerMenu) opens the slide-in nav;
			// emit the linkClick with type `sidebar`, description `open`
			// (dealer|sidebar|1|n/a|open). Sidebar CLOSE fires a
			// ddc.dialog.closed carrying no field that distinguishes it from a
			// content overlay (empty dialogTitle/trackingId, no widgetName), so
			// it is left to the generic overlay-close rule (US2046701) rather
			// than tagged here. Numeric `location` is deferred (US1906349).
			//
			// This rule sits ahead of the legacy hamburger navClick rule, so
			// first-match-wins makes that legacy beacon unreachable while the flag
			// is on. US2093316: that is now the INTENT — Publicis confirmed the
			// granular linkClick replaces the legacy navClick rather than joining
			// it — so the legacyHamburgerNavClick trigger that used to re-emit it
			// alongside this event (TC1029576) has been removed. Flag off is
			// unchanged: this rule doesn't match and the legacy rule fires directly.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.isHamburgerMenu === true,
				payload: {
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'sidebar',
						location: 1,
						title: 'n/a',
						description: 'open'
					}
				}
			},
			// US2055082 (SDG 5.4.2.11) — mobile sidebar SUBMENU open/close. The
			// hamburger slide-in's submenu group toggles emit no ddc.link.generic
			// on their own (the DDC header script stops the click's propagation);
			// ws-tracking (US2055082) catches them with a capture-phase delegate
			// and emits ddc.link.generic marked additively with
			// navContext:'sidebar' and navToggleState 'open'|'close'. navContext
			// uniquely identifies these (only that path sets it), so key on it
			// alone. type sidebar; description = slug(label) + '-' +
			// navToggleState. ws-tracking derives the direction from the toggle's
			// collapse panel `nav-visible` class (the toggle's aria-expanded is
			// stale on this template and the accordion ids are duplicated across
			// the footer/slide-in copies, so neither is usable here). Flag-gated;
			// off keeps prior behavior. Numeric `location` is 1 (US2061364).
			{
				logic: ({ eventData: { navContext } = {}, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) && navContext === 'sidebar',
				payload: ({
					eventData: { text = '', navToggleState } = {}
				}) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'sidebar',
						location: 1,
						title: 'n/a',
						description: `${toAdobeLinkNameSlug(text)}-${
							navToggleState === 'close' ? 'close' : 'open'
						}`
					}
				})
			},
			// US2046694 (SDG 5.4.2.11) — net-new GRANULAR desktop nav dropdown
			// OPEN/CLOSE. The top-nav megamenu toggles fire ddc.link.generic
			// with data-toggle="dropdown" (trackingSection Global Nav, location
			// mainNav). DDC captures the element's PRE-toggle aria-expanded, so
			// "false" => opening and "true" => closing; the eVar75 <component
			// text> is the slugged label suffixed with -open / -close (e.g.
			// new-open, new-close). type is `header` per the SDG header
			// taxonomy. The mobile sidebar submenu groups are ajax-injected and
			// fire no DDC link event, so they are not covered here. Numeric
			// `location` is deferred (US1906349).
			//
			// This rule sits ahead of the legacy dropdown navClick rule, so
			// first-match-wins makes that legacy beacon unreachable while the flag
			// is on. US2093316: that is now the INTENT (see the hamburger rule
			// above), so the legacyDropdownNavClick trigger has been removed. Flag
			// off is unchanged: this rule doesn't match and the legacy rule fires
			// directly.
			{
				logic: ({
					eventData: {
						attributes = {},
						location,
						trackingSection
					} = {},
					flags
				} = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					attributes?.['data-toggle'] === 'dropdown' &&
					location === 'mainNav' &&
					trackingSection === 'Global Nav',
				payload: ({
					eventData: { text = '', attributes = {} } = {}
				}) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'header',
						location: 1,
						title: 'n/a',
						description: `${toAdobeLinkNameSlug(text)}-${
							attributes?.['aria-expanded'] === 'true'
								? 'close'
								: 'open'
						}`
					}
				})
			},
			// US2046694 (SDG 5.4.2.3) — net-new GRANULAR footer link
			// interaction. Footer links (location `footer`) were untagged;
			// emit the linkClick with type `footer`, description = slugged
			// link text, linkUrl = href (dealer|footer|1|n/a|<text>). Footer
			// accordion group headers ("Shopping Tools" etc.) double as links
			// and carry no reliable open/close signal in the data layer
			// (identical events, `collapsed` class on both, no aria-expanded),
			// so they are tagged as footer links here rather than as 5.4.2.11
			// open/close. Keyed on location:'footer' so it covers both mobile
			// (ws-navigation) and desktop (links-list) footer widgets
			// (TA4168416). Gated behind the flag; off keeps footer links
			// untagged (prior behavior). A footer link also fires
			// ddc.link.navClick (legacy navClick) which is left intact,
			// mirroring the shipped getDirections footer handling. On the
			// env/nonprod merge this rule must sit AFTER the getDirections /
			// clickToCall rules so a footer directions/phone link still gets
			// its specific event. Numeric `location` deferred (US1906349).
			{
				logic: ({ eventData: { location } = {}, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) && location === 'footer',
				payload: ({
					eventData: { text = '', url = '', widgetHeading = '' } = {}
				}) => ({
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'footer',
						location: 1,
						title: widgetHeading
							? toAdobeLinkNameSlug(widgetHeading)
							: 'n/a',
						description: toAdobeLinkNameSlug(text),
						linkUrl: url
					}
				})
			},
			// this block needs to be before the next one, as hamburgers usually have class 'btn'
			{
				logic: {
					isHamburgerMenu: true
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: 'nav open',
						position: 'sidebar'
					}
				}
			},
			// US2046689: Hero / Special Offers rotator (ws-specials) CTA
			// interactions — SDG 5.4.2.2 (CTA) + Hero Component 5.4.1.3.
			// Covers the slide-image click and the offer CTA button on the
			// homepage rotator. The rotator's controls (next/previous
			// arrows, play/pause, index dots) ALSO fire ddc.link.generic
			// from ws-specials, but with an empty `url` — the `Boolean(url)`
			// guard keeps them out (only navigating clicks are CTAs, and
			// per the SDG a CTA carries linkUrl). Must precede the generic
			// `.btn` CTA rule below so offer buttons get the specials
			// taxonomy instead of the legacy linkText payload. Gated behind
			// ws-tagging-stellantis-adobe-updates; when off, buttons fall
			// through to the legacy rule and image clicks stay untagged
			// (prior behavior). component `type` is `specials` per the
			// widget->type table in US1906349 (ws-specials -> specials).
			// `location` is the widget's numeric position (US2061364); `title`
			// has no source on the event so it defaults to 'n/a'
			// (e.g. dealer|specials|1|n/a|shop-inventory).
			//
			// TA4132852: for an image-only slide anchor whose <img> has no
			// alt/title, DDC's getLinkText falls back to the image `src`
			// for `text` (chain: text nodes -> alt -> title -> src), so the
			// slug becomes the raw image URL — unusable as the eVar75 Link
			// Name. When `text` is a URL, prefer the anchor's aria-label
			// (carried on eventData.attributes) as the readable source.
			// Text-bearing offer buttons and alt-bearing slides keep their
			// own `text`. If neither `text` nor aria-label is usable we
			// still emit the src (accepted last-resort, per product call).
			{
				logic: ({ eventData: { widgetName, url } = {}, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					widgetName === 'ws-specials' &&
					Boolean(url),
				payload: ({ eventData = {} } = {}) => {
					const { text, url, attributes = {} } = eventData;
					const isUrlText = /^https?:\/\//i.test(text);
					const ariaLabel = attributes['aria-label'];
					return {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes: {
							site: 'dealer',
							type: getComponentType({ eventData }),
							location: getComponentLocation({ eventData }),
							title: 'n/a',
							description: toAdobeLinkNameSlug(
								isUrlText && ariaLabel ? ariaLabel : text
							),
							linkUrl: url
						}
					};
				}
			},
			// US2048395 (SDG 5.4.2.3) — net-new GRANULAR Carousel Index
			// Selection for the recommended-vehicles carousel (ws-rec-vehicles):
			// the paging dots under the slides. component `type` is
			// `vehicle-selection` per the widget->type table in US1906349
			// (ws-rec-vehicles -> vehicle-selection); `description` is
			// `index-<slide number>` per the story's payload spec
			// (e.g. dealer|vehicle-selection|1|featured-vehicles|index-2).
			// `location` is the widget's numeric position among the visible
			// vehicle-selection widgets (US2061364).
			//
			// The dots are MOBILE-ONLY — the desktop template renders the
			// recommendations as a flex-wrap grid (data-testid
			// static-width-carousel) with no paging at all.
			//
			// Keying on the aria-label is deliberate, not a shortcut: a dot is a
			// <button> wrapping an <svg>, so DDC reports BOTH `text` and `url`
			// empty and the slide number exists nowhere else on the event. The
			// widget scope matters too — the same "View Slide N" label shape
			// appears on other carousels, and model-selector-responsive's index
			// paging belongs to US2046690.
			//
			// A swipe pages the same carousel but emits no ddc.link.generic (only
			// a widgetUserEvent, action 'swiped'), so it is untagged here; this
			// story is scoped to the index buttons.
			//
			// `title` is the slugged widget heading because this widget appears
			// twice with different headings — "Featured Vehicles" on the homepage
			// and "Vehicles You Might Like" on a VDP — and eVar75 has to tell
			// them apart. Falls back to 'n/a' when the widget has no heading.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.widgetName === 'ws-rec-vehicles' &&
					/^view slide \d+$/i.test(
						eventData?.attributes?.['aria-label'] || ''
					),
				payload: ({ eventData = {} } = {}) => {
					const { widgetHeading, attributes = {} } = eventData;
					return {
						type: 'CustomTagEvent',
						eventName: 'linkClick',
						eventAction: 'linkClick',
						attributes: {
							site: 'dealer',
							type: getComponentType({ eventData }),
							location: getComponentLocation({ eventData }),
							title: widgetHeading
								? toAdobeLinkNameSlug(widgetHeading)
								: 'n/a',
							description: `index-${attributes[
								'aria-label'
							].match(/\d+/)}`
						}
					};
				}
			},
			// US2046690 — VDP CTA interactions. Three surfaces the client flagged as
			// missing CTA events, all reported as `CTA`/`buttonClick` with the
			// granular attributes:
			//   * ws-vehicle-ctas buttons (Confirm Availability, Calculate My
			//     Payment, ...). These carry a `btn` class token, so the legacy
			//     generic `.btn` rule further down ALREADY tags them — this rule
			//     sits ahead of it so a flag-on click emits ONE granular event
			//     instead of two (first-match-wins). Flag off is unchanged.
			//   * Explore All Offers in the Price details panel
			//     (`multiple-incentives-link`), previously untagged.
			//   * the VDP Track Price button. US2046693's Track Price rule is
			//     SRP-scoped (it bails unless isSRP and ws-inv-listing), so the VDP
			//     copy was untagged.
			// `type` is `vehicle-inventory` and `title` is the vehicle slug, matching
			// US2046693 — these are all vehicle-scoped CTAs and none of the three
			// widgets appears in the US1906349 widget->type table. `linkUrl` is only
			// reported when the CTA actually navigates; Explore All Offers and Track
			// Price open overlays and carry no href. Telephonic CTAs are excluded so
			// a phone number stays with the click-to-call path (US2046695). Numeric
			// `location` is deferred (US1906349).
			{
				logic: ({ eventData, flags } = {}) => {
					if (
						!isAdobeUpdatesOn({ flags }) ||
						eventData?.isTelephonicUrl === true
					) {
						return false;
					}

					const { attributes = {}, widgetName } = eventData || {};
					const classes = attributes?.class?.split?.(' ') || [];

					return (
						widgetName === 'ws-vehicle-ctas' ||
						classes.includes('multiple-incentives-link') ||
						(widgetName === 'ws-mycars-vdp' &&
							attributes['data-testid'] === 'button-trackPrice')
					);
				},
				payload: ({ eventData = {} } = {}) => {
					const { text, url, vehicle } = eventData;
					const attributes = {
						site: 'dealer',
						type: 'vehicle-inventory',
						location: eventData.widgetName
							? getComponentLocation({
									eventData,
									type: 'vehicle-inventory'
							  })
							: 1,
						description: toSlug(text),
						title: getVehicleTitle(vehicle)
					};

					if (url) {
						attributes.linkUrl = url;
					}

					return {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes
					};
				}
			},
			// US2046690 — model-selector carousel paging buttons, i.e. the index
			// dots. These exist ONLY on the mobile template (desktop shows
			// body-style tabs and renders no dots at all), where the carousel shows
			// one model per slide.
			// Reported as CTA/buttonClick per the story's payload spec, consistent
			// with the other US2046690 events.
			// The widget emits ddc.link.generic for four different clicks, so match
			// precisely: a paging button is the one carrying `role: 'tab'`, and its
			// `text` is the 1-based index. The model tiles carry a /new-inventory/
			// url, the body-style selector carries url '#' (US2046700 owns that one),
			// and the next/previous arrows carry a slick-next/slick-prev class.
			// `description` is `index-<n>` and `title` is the component heading.
			// Numeric `location` deferred (US1906349).
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.widgetName === 'model-selector-responsive' &&
					eventData?.attributes?.role === 'tab' &&
					/^\d+$/.test(`${eventData?.text ?? ''}`.trim()),
				payload: ({ eventData = {} } = {}) => {
					const { text, widgetHeading } = eventData;
					return {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes: {
							site: 'dealer',
							type: getComponentType({ eventData }),
							location: getComponentLocation({ eventData }),
							title: widgetHeading
								? toSlug(widgetHeading)
								: 'n/a',
							description: `index-${`${text}`.trim()}`
						}
					};
				}
			},
			// US2046690 (SDG 5.4.1.7) — the model TILE inside the model-selector
			// carousel: the vehicle image + model name that navigates to a filtered
			// inventory search. The SDG's Vehicle Selection Component spells this
			// exact beacon out — `dealer|vehicle-selection|1|browse-new-models|compass`
			// — i.e. `description` is the MODEL NAME and `title` is the component
			// heading. Reported as CTA/buttonClick to stay consistent with the rest
			// of US2046690 (the paging buttons above, the VDP CTAs).
			//
			// Live capture on bcfregression-stellantis (2026-08-26) shows what a
			// single tile click actually pushes:
			//   desktop -> widgetUserEvent (model-selector, 'Navigated to model
			//              listings (SRP/VLP)') AND ddc.link.generic
			//   mobile  -> ddc.link.generic ONLY
			//
			// US2093316: this rule used to be gated on isMobile(). That gate existed
			// solely to avoid double-counting against the desktop widgetUserEvent,
			// which was tagged further up this file as CTA/buttonClick with the
			// legacy `linkText: 'browse new models <model>'`. That legacy rule is now
			// suppressed with the flag on, so the gate is obsolete — and leaving it
			// in would have made a DESKTOP model tile click emit nothing at all,
			// which is the one real coverage hole the teardown would otherwise have
			// opened. Device-agnostic is now the correct scope: exactly one granular
			// event on both devices. Flag off is unchanged (this rule requires the
			// flag, and the legacy desktop beacon still fires).
			//
			// Matching: this widget fires ddc.link.generic for four different
			// clicks, and the tile is the only one that NAVIGATES — a truthy `url`
			// that isn't the '#' of the body-style selector (US2046700's
			// widgetUserEvent owns that). The `role: 'tab'` and slick-arrow guards
			// are deliberately redundant: the paging buttons and next/previous
			// arrows report `url: ''` today, but this rule sits ABOVE the legacy
			// slick-arrow rule, so should a future carousel version give an arrow an
			// href, first-match-wins would silently delete the arrow's
			// "browse new models next" beacon (the recurring defect on this
			// project). Excluding them here makes that impossible.
			//
			// `linkUrl` is reported because the tile leads to an internal page, per
			// the CTA Interaction Event template. `description` comes from the
			// tile's visible model name. The tile is TWO sibling anchors sharing one
			// href — an image-only one and a text one — but unlike the hero slides in
			// TA4132852 there is no image-URL leak to guard against: the image
			// carries alt="<model>", so DDC's getLinkText resolves 'Charger' for
			// either anchor (verified in the same capture).
			{
				logic: ({ eventData, flags } = {}) => {
					if (!isAdobeUpdatesOn({ flags })) {
						return false;
					}

					const {
						widgetName,
						url,
						attributes = {}
					} = eventData || {};
					const classes = attributes?.class || '';

					return (
						widgetName === 'model-selector-responsive' &&
						Boolean(url) &&
						url !== '#' &&
						attributes.role !== 'tab' &&
						!/slick-(next|prev)/.test(classes)
					);
				},
				payload: ({ eventData = {} } = {}) => {
					const { text, url, widgetHeading } = eventData;
					return {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes: {
							site: 'dealer',
							type: getComponentType({ eventData }),
							location: getComponentLocation({ eventData }),
							title: widgetHeading
								? toAdobeLinkNameSlug(widgetHeading)
								: 'n/a',
							description: toAdobeLinkNameSlug(text),
							linkUrl: url
						}
					};
				}
			},
			// US2046693 (SDG 5.4.2.2) — net-new GRANULAR CTA interactions on the SRP
			// inventory card (ws-inv-listing). The client's 7/9/2026 WSP 2.0
			// feedback deck marks these elements under its CTA Interaction Events
			// section for this story:
			//   - Track Price (opens the price-alert overlay; the deck's bullet is
			//     "Overlays are missing CTA events")
			//   - the card footer's Details link
			//   - the card's CTA buttons (Confirm Availability, Get Pre Approved,
			//     View Window Sticker, ...), which DDC flags with isSrpVehicleCta
			// The card's pricing panel — the Pricing/Info tabs and the incentive
			// `Details` expanders — is deliberately NOT matched here: the deck puts
			// those under its Link Interaction section as US2046692.
			// The card's main photo is also NOT matched here: SDG 5.4.1.12 defers
			// imagery to the Media Interaction Event, so US2046691 tags it as
			// `media`/`interaction` with assetType/assetSource/assetName. Do not
			// re-add a `containsImage` case to this rule.
			// `type` is `vehicle-inventory` per the SDG's Vehicle Listing Inventory
			// component (the US1906349 widget->type table has no ws-inv-listing
			// row). `title` is the vehicle, slugged.
			// Placed before the legacy `.btn` rule below: with the flag ON a card
			// CTA emits ONE granular CTA event instead of the legacy `linkText`
			// shape; with the flag OFF the legacy rule keeps firing unchanged and
			// the Track Price/Details clicks stay untagged, as today.
			// Telephonic CTAs are excluded so a phone number on a card stays with
			// the click-to-call path (US2046695).
			// `location` is the widget's numeric position (US2061364).
			{
				logic: ({ eventData, flags } = {}) => {
					if (
						!isAdobeUpdatesOn({ flags }) ||
						eventData?.isSRP !== true ||
						eventData?.widgetName !== 'ws-inv-listing' ||
						eventData?.isTelephonicUrl === true
					) {
						return false;
					}

					const { attributes = {} } = eventData;
					const classes = attributes?.class?.split?.(' ') || [];

					return (
						eventData?.isSrpVehicleCta === true ||
						attributes['data-testid'] === 'button-trackPrice' ||
						classes.includes('modern-my-cars-details-label')
					);
				},
				payload: ({ eventData = {} } = {}) => {
					const { text, url, vehicle } = eventData;
					const attributes = {
						site: 'dealer',
						type: getComponentType({ eventData }),
						location: getComponentLocation({ eventData }),
						description: toSlug(text),
						title: getVehicleTitle(vehicle)
					};

					// linkUrl is only reported when the CTA navigates — Track Price
					// opens an overlay and carries no href.
					if (url) {
						attributes.linkUrl = url;
					}

					return {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes
					};
				}
			},
			// US2046698: Header home logo. The home-icon link (class
			// home-logolink) and the dealer logo (class header-logo) both
			// navigate home and were untagged; emit the Navigate-to-Home
			// linkClick (SDG 5.4.2.3 / 5.4.2.4). Match the specific logo
			// classes rather than the whole header-default widget (which also
			// hosts non-logo links). Gated behind
			// ws-tagging-stellantis-adobe-updates. The "Home" nav link keeps
			// its navClick and the contact-info dealer-name link stays
			// untagged. Numeric `location` is 1 (US2061364).
			{
				logic: ({
					eventData: { attributes = {} } = {},
					flags
				} = {}) => {
					const classes = attributes?.class?.split?.(' ') || [];
					return (
						isAdobeUpdatesOn({ flags }) &&
						(classes.includes('home-logolink') ||
							classes.includes('header-logo'))
					);
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'linkClick',
					eventAction: 'linkClick',
					attributes: {
						site: 'dealer',
						type: 'header',
						location: 1,
						description: 'home',
						title: 'n/a',
						linkUrl: '[[eventData.url]]'
					}
				}
			},
			// US2046691 (SDG 5.4.2.13) — a click on an inventory card's image is
			// a MEDIA interaction, not a CTA. The SDG's Vehicle Listing Inventory
			// component defers imagery to the Media Interaction Event, whose own
			// eVar75 example is
			// `dealer|vehicle-inventory|1|2026-chrysler-pacifica-select-awd|image-click`
			// (trailing token built server-side from <assetType>-<assetAction>).
			// The card's CTA buttons / Track Price / Details stay CTA (US2046693).
			// `location` is the widget's numeric position (US2061364).
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.isSRP === true &&
					eventData?.containsImage === true &&
					eventData?.widgetName === 'ws-inv-listing',
				// `element` is the clicked node, passed alongside eventData — the
				// engine blanks `eventData.element` to keep that object
				// serializable. Same access pattern as forddirectnative's
				// getVehicleDataFromElement.
				payload: ({ element, eventData = {} } = {}) => {
					const { vehicle } = eventData;
					const { assetName, assetSource } = getClickedImageAsset({
						element,
						vehicle
					});

					return {
						type: 'CustomTagEvent',
						eventName: 'media',
						eventAction: 'interaction',
						attributes: {
							site: 'dealer',
							type: getComponentType({ eventData }),
							location: getComponentLocation({ eventData }),
							title: getVehicleTitle(vehicle),
							description: 'click',
							assetType: 'image',
							assetSource,
							assetName
						}
					};
				}
			},
			// US2046691 — with the flag ON, a VDP gallery photo click reports as
			// ONE `media` interaction (emitted from the widgetUserEvent stream),
			// so the legacy `linkText` CTA this same click also produces must be
			// dropped or Adobe counts event70 twice for one interaction. A
			// matched rule with `payload: undefined` sends nothing (see
			// dataMapper's `if (rule && rule.payload)`); precedent is asc's own
			// nav-click suppression. Must stay BEFORE the legacy `.btn` rule.
			// Flag OFF: this rule never matches and today's CTA is unchanged.
			// Scoped to `containsImage` so the gallery's other controls, which do
			// not emit ddc.link.generic today, cannot be silenced by accident.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					GALLERY_WIDGETS.has(eventData?.widgetName) &&
					eventData?.containsImage === true,
				payload: undefined
			},
			// US2093316 (SDG 5.4.2.1 / 5.4.2.2) — GRANULAR CTA catch-all: the
			// 2.0-shaped replacement for the legacy `.btn` CTA rule directly below.
			// That legacy rule is what currently satisfies the SDG's requirement
			// that "all clickable elements are expected to be tagged" for any
			// button with no specific mapping, so retiring it without this would
			// have been a coverage regression rather than a cleanup.
			//
			// The matcher is a deliberate character-for-character copy of the
			// legacy rule's, so coverage is provably identical and ONLY the payload
			// shape changes. Sitting immediately above it means first-match-wins
			// makes the legacy rule unreachable with the flag on, and untouched
			// with the flag off.
			//
			// `type` resolution, in order:
			//   1. the US1906349 widget->type table (getComponentType)
			//   2. the DDC region carried on the event — header / mainNav -> header,
			//      footer -> footer — the same fallback the getDirections rule uses
			//   3. 'feature'. NOT invented: the SDG defines its Feature Component
			//      (5.4.1.8) as "Component sections that contains feature or
			//      dealership information on a dealership page", the widget table
			//      already maps image-default / links-list / ws-content-cta /
			//      ws-why-buy to 'feature', and the client's 7/9/2026 feedback deck
			//      uses dealer|feature|1|featued-vehicles|... and
			//      dealer|feature|2|n/a|shop-pre-owned-invetory for exactly this
			//      kind of generic CTA. Flagged for client confirmation all the
			//      same — see docs/stellantis-wsp2-legacy-teardown.md.
			//
			// `location` falls back to 1: getComponentLocation classifies its
			// candidates through that same widget table, so it returns undefined
			// for any widget outside it (and for a region type by design).
			//
			// Telephonic links are EXCLUDED, matching US2046690's and US2046693's
			// granular CTA rules, so a phone falls through to the granular
			// clickToCall rule below and emits exactly one event. This retires
			// TC1029576's CTA-plus-clickToCall pairing for a `.btn` phone (the
			// header "Call" button): that pairing existed to stop the granular
			// rollout from DELETING the legacy CTA while both taxonomies ran
			// together, and with the legacy shape gone it is a straight
			// double-count of event70 — the Click to Call event already reports
			// event28 AND event70 plus the full site/type/location/title/
			// description taxonomy in eVar75, so nothing is lost by dropping it.
			// Flag off is unchanged: the legacy rule below still pairs them.
			//
			// One deliberate loss, documented for the client: the legacy payload
			// prefixed its linkText with `custom-factory-orders:` /
			// `power-brokers:` on those two SiteBuilder pages. The granular
			// taxonomy has no slot for a page prefix — the page is identified by
			// templateId / pageName on the page-load beacon instead.
			{
				logic: ({ eventData, flags } = {}) => {
					if (!isAdobeUpdatesOn({ flags })) {
						return false;
					}

					if (eventData?.isTelephonicUrl === true) {
						return false;
					}

					const {
						attributes = {},
						trackingSection = '',
						widgetName = ''
					} = eventData || {};
					const classes = attributes?.class?.split?.(' ') || [];

					return (
						(classes.includes('btn') ||
							trackingSection === 'Share' ||
							widgetName === 'ws-content-cta') &&
						!classes.includes('facetmulti-freeform-search-submit')
					);
				},
				payload: ({ eventData = {} } = {}) => {
					const { location, text, url, widgetHeading } = eventData;
					const type =
						getComponentType({ eventData }) ||
						REGION_COMPONENT_TYPE[location] ||
						'feature';
					const attributes = {
						site: 'dealer',
						type,
						location:
							getComponentLocation({ eventData, type }) || 1,
						title: widgetHeading
							? toAdobeLinkNameSlug(widgetHeading)
							: 'n/a',
						description: toAdobeLinkNameSlug(text)
					};

					if (url) {
						attributes.linkUrl = url;
					}

					return {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes
					};
				}
			},
			// US2093316: with the flag ON the granular catch-all above shadows this
			// rule for every NON-telephonic match. Telephonic links are excluded
			// from the catch-all (one event per phone, not CTA + clickToCall), and
			// first-match-wins then made this legacy rule reachable again for a
			// `.btn` phone — caught by the live suite, which saw the header phone
			// emit legacy CTA/buttonClick alongside the granular clickToCall. So
			// the payload is gated: with the flag on this rule still MATCHES a
			// phone (which is what keeps its telephonicURL trigger firing, and
			// therefore the one granular clickToCall) but emits nothing itself.
			// Everything below describes its flag-OFF behavior, which is unchanged.
			// TC1029576: this CTA rule stays AHEAD of the granular click-to-call
			// rule below. A phone link that is also a `.btn` (e.g. the header
			// "Call" button) must keep firing CTA/buttonClick — it did before
			// US2046695, and rule evaluation is first-match-wins, so putting the
			// granular rule first silently deleted that event. Its
			// `telephonicURL` trigger now carries the granular payload (see
			// triggerEvents below), so such a phone emits CTA/buttonClick PLUS
			// exactly one granular clickToCall. Non-`btn` phones (contact-info)
			// don't match here and fall through to the granular rule instead, so
			// no surface loses coverage and nothing is double-counted.
			//
			// It also stays BEHIND the ws-specials hero rule above, which needs
			// offer buttons to get the specials taxonomy rather than this legacy
			// linkText payload. Order: hero -> generic CTA -> granular clickToCall.
			{
				logic: ({
					eventData: {
						attributes = {},
						trackingSection = '',
						widgetName = ''
					} = {}
				}) =>
					(attributes?.class?.split?.(' ').includes('btn') ||
						trackingSection === 'Share' ||
						widgetName === 'ws-content-cta') &&
					!attributes?.class
						?.split?.(' ')
						.includes('facetmulti-freeform-search-submit'),
				payload: legacyPayload(({ eventData: { pageName, text } }) => {
					const linkText = collapsePipes(text);

					const payload = {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes: {
							linkText
						}
					};

					if (pageName === 'SITEBUILDER_CUSTOM_FACTORY_ORDERS_1') {
						payload.attributes.linkText = `custom-factory-orders:${linkText?.toLowerCase?.()}`;
					} else if (
						pageName === 'SITEBUILDER_DODGE_POWER_BROKER_1'
					) {
						payload.attributes.linkText = `power-brokers:${linkText?.toLowerCase?.()}`;
					}

					return payload;
				}),
				trigger: 'telephonicURL'
			},
			// US2046695 (SDG 5.4.2.5 / 5.4.2.5.1) — net-new GRANULAR click-to-call.
			// Covers every telephonic link that does NOT match the CTA rule above
			// (dealer-information / contact-info phones, footer, Contact Us
			// overlay); phones that DO match it get the identical granular payload
			// from the telephonicURL trigger instead. Gated behind
			// ws-tagging-stellantis-adobe-updates: with the flag off this rule is
			// skipped and the legacy CTA + bare-department telephonicURL trigger
			// keep firing exactly as before.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.isTelephonicUrl === true,
				payload: ({ eventData = {} } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'clickToCall',
					eventAction: 'pageElementInteraction',
					attributes: getGranularClickToCallAttributes(eventData)
				})
			},
			{
				logic: {
					'eventData.widgetName': 'ws-media-viewer',
					'eventData.attributes.class': 'pswp__button--arrow--right'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'modal:gallery:next-button'
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'ws-media-viewer',
					'eventData.attributes.class': 'pswp__button--arrow--left'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'modal:gallery:prev-button'
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'ws-media-viewer',
					'eventData.attributes.class': 'pswp__button--close'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'modal:gallery:close'
					}
				}
			},
			// US2093316: the legacy tail below is MIXED, so it is gated per rule with
			// legacyPayload() rather than by one suppressor -- a suppressor here would
			// take down the rules that must keep firing, and reordering the tail to
			// separate them would risk changing flag-off behavior (these matchers are
			// substring-based and their relative order is load-bearing).
			//   OFF with the flag on: the desktop dropdown and mobile panel
			//     navClicks (replaced by 5.4.2.11), the contact-info directions link
			//     (replaced by 5.4.2.6) and the map-dynamic general beacon (replaced
			//     by 5.4.2.12 plus the granular getDirections).
			//   STILL FIRING: the mainNav fragment CTA, the slick carousel arrows and
			//     content-default -- WSP 2.0 defines events for all three (5.4.2.2 /
			//     5.4.2.3 / 5.4.2.8) but none is built for those surfaces, and the
			//     granular CTA catch-all above only reaches elements carrying `btn`.
			//   STILL FIRING (above this line): the ws-media-viewer CMS *content*
			//     gallery -- GALLERY_WIDGETS covers only ws-vehicle-media and
			//     ws-vehicle-gallery.
			// this is for top level nav clicks off the main nav on desktop (but just in case position is dynamic)
			{
				logic: {
					'eventData.attributes.data-toggle': 'dropdown',
					trackingSection: 'Global Nav',
					location: 'mainNav'
				},
				// US2093316: OFF with the flag on -- the granular dropdown
				// open/close linkClick (5.4.2.11) above replaces it.
				payload: legacyPayload({
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.text]]',
						position: isMobile() ? 'sidebar' : 'topNav'
					}
				})
			},
			// and the following is for mobile, but just in case the position is dynamic
			{
				// US2055082: exclude the mobile sidebar submenu toggles that
				// ws-tracking now emits with navContext 'sidebar' — the granular
				// sidebar rule above handles them when the flag is on, and they
				// must stay untagged when it is off (this legacy navClick would
				// otherwise start firing for them, changing flag-off behavior).
				logic: ({
					eventData: { attributes = {}, navContext } = {}
				} = {}) =>
					attributes?.class ===
						'no-follow panel-heading-link panel-toggle' &&
					navContext !== 'sidebar',
				// US2093316: OFF with the flag on -- the granular sidebar submenu
				// open/close linkClick (US2055082, 5.4.2.11) above replaces it.
				payload: legacyPayload({
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.text]]',
						position: isMobile() ? 'sidebar' : 'topNav'
					}
				})
			},
			// this is for fragment widget clicks in the main nav megamenu on desktop
			{
				logic: ({ eventData }) => {
					return (
						!['ws-navigation', 'navigation-default'].includes(
							eventData?.widgetName
						) && eventData?.location === 'mainNav'
					);
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: '[[eventData.text]]'
					}
				}
			},
			{
				logic: ({ eventData }) =>
					(eventData?.widgetName === 'model-selector-responsive' &&
						// not ideal but better than attaching to a label
						// also we probably will not be updating this old model selector carousel library
						eventData?.attributes?.class?.includes?.(
							'slick-next'
						)) ||
					eventData?.attributes?.class?.includes?.('slick-prev'),
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: ({ eventData }) =>
							`browse new models ${
								eventData?.attributes?.class?.includes?.('next')
									? 'next'
									: 'previous'
							}`
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'content-default'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: '[[eventData.text]]'
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'contact-info',
					'eventData.url': '/dealership/directions.htm'
				},
				// US2093316: OFF with the flag on -- the granular getDirections rule
				// (5.4.2.6) at the top of this array matches the same
				// /dealership/directions.htm click and reports it with the full
				// taxonomy.
				payload: legacyPayload({
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText:
							'[[eventData.text]] >> [[eventData.innerText]]'
					}
				})
			},
			{
				logic: {
					'eventData.widgetName': 'map-dynamic'
				},
				// US2093316: OFF with the flag on -- map interactions are reported by
				// the 5.4.2.12 granular map events (ddc.map.*) and the map's
				// directions anchor by the granular ddc.link.getDirections rule.
				payload: legacyPayload({
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'map'
					}
				})
			}
		],
		// US2046697 — a map interaction (drag). New event from ws-tracking.
		'ddc.map.pan': [
			{
				logic: isFlaggedMapInteraction,
				payload: mapInteractionPayload
			}
		],
		// US2046697 — a map interaction (zoom/pinch). New event from ws-tracking.
		'ddc.map.zoom': [
			{
				logic: isFlaggedMapInteraction,
				payload: mapInteractionPayload
			}
		],
		// US2046697 — a map interaction (Map/Satellite/Terrain view toggle).
		'ddc.map.viewChange': [
			{
				logic: isFlaggedMapInteraction,
				payload: mapInteractionPayload
			}
		],
		// US2046697 — a map interaction (fullscreen enter/exit).
		'ddc.map.fullscreen': [
			{
				logic: isFlaggedMapInteraction,
				payload: mapInteractionPayload
			}
		],
		'ddc.map.pin': [
			// US2046697 — flag on: pin joins zoom/pan on the 5.4.2.12 taxonomy.
			{
				logic: isFlaggedMapInteraction,
				payload: mapInteractionPayload
			},
			// flag off: legacy beacon (US1517599, spec v11.0) — unchanged.
			{
				logic: {
					'eventData.widgetName': 'map-dynamic'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'map'
					}
				}
			}
		],
		'ddc.link.getDirections': [
			// US2046696: map-dynamic "Get Directions" (SDG 5.4.2.6). ws-tracking
			// emits ddc.link.getDirections for the map's "Visit us at" anchor
			// (carrying widgetName + the anchor text), matched via the
			// data-ws-tracking-directions hook cms-web puts on it. Both upstream
			// halves are landed (cms-web #6835, ws-tracking #193). Emit the
			// granular shape
			// (type dealer-information per the US1906349 widget->type table).
			//
			// `location` is a hardcoded 1, NOT a counted ordinal (US2061364).
			// The WSP 2.0 SDG (4-14-2026) Directions Event section documents
			// this exact interaction as `dealer|dealer-information|1|n/a|
			// get-directions` — location 1, title n/a. dealer-information is one
			// component that INCLUDES the map ("dealer information in the body
			// of the page, usually with a map for directions"), so the map must
			// not be counted against the page's other dealer-information
			// widgets. Do not replace this with getComponentLocation: that
			// counts DDC widgets (contact-info, map-dynamic, ws-hours are
			// three) and yields 2 or 3. Same reasoning as mapInteractionPayload.
			//
			// Flag-gated and placed before
			// the legacy linkText rule so flag-off keeps the prior behavior.
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.widgetName === 'map-dynamic',
				payload: ({ eventData = {} } = {}) => ({
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						site: 'dealer',
						type: getComponentType({ eventData }),
						location: 1,
						title: eventData.widgetHeading
							? toAdobeLinkNameSlug(eventData.widgetHeading)
							: 'n/a',
						description: toAdobeLinkNameSlug(eventData.text)
					}
				})
			},
			// US2093316: the legacy linkText getDirections fallback. It has no logic, so
			// it matches every ddc.link.getDirections emit -- unlike the ddc.map.pin
			// legacy rule, it is NOT shadowed by the granular rule above (which requires
			// widgetName map-dynamic), and so it needs an explicit suppressor.
			SUPPRESS_LEGACY,
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.linkText]]'
					}
				}
			}
		],
		// US2093316 (SDG 4.13 / 5.4.2.7) — Social Interactions. ws-tracking emits
		// ddc.link.socialHeaderClick for the header icon row and
		// ddc.link.socialShare for the footer / share widgets; both carry
		// eventData.service. Adobe records event30 (Social Interaction) alongside
		// event70. Flag-gated: with the flag off these clicks keep whatever they
		// get today from the legacy `.btn` CTA rule (trackingSection 'Share'), so
		// nothing changes.
		'ddc.link.socialHeaderClick': [
			{
				logic: isAdobeUpdatesOn,
				payload: socialPayload({ fixedType: 'header' })
			}
		],
		'ddc.link.socialShare': [
			{
				logic: isAdobeUpdatesOn,
				payload: socialPayload({ defaultType: 'footer' })
			}
		],
		// US2093316 (SDG 4.18) — Error Display, form validation only.
		// ws-tracking emits ddc.form.submission with validate:false and an
		// invalidFields map when client-side validation blocks a submit;
		// shiftnative already taps exactly this signal
		// (src/mappings/shiftnative/event/default.js).
		//
		// The SDG allows three `type` values — "form validation", "no results
		// found" and "unknown" — but only describes where the first one fires
		// ("usually when a user does not put correct information on a form"), so
		// that is the only case implemented. Where the other two belong is an open
		// client question (docs/stellantis-wsp2-legacy-teardown.md).
		//
		// INVENTORY_SEARCH is excluded for the same reason shiftnative excludes
		// it: an SRP facet search that returns nothing is not a validation error,
		// and it would fire this on nearly every search.
		//
		// This is the one WSP 2.0 event with no granular taxonomy — the SDG gives
		// it only `message` and `type`, and defines NO eVar mapping for it at all.
		//
		// The BusinessLink page keeps its OWN copy of this rule in
		// event/default/SITEBUILDER_WORK_READY_1.js — un-gated, and reporting
		// `form-validation` rather than `form validation` because the
		// BusinessLink spec supplies the eVar104 mapping the SDG omits. That
		// copy is `unshift`ed, so it wins on that page whatever this flag does;
		// only one rule per event ever fires (dataMapper getRule uses `find`).
		// Leave this rule as the sitewide default rather than narrowing it.
		'ddc.form.submission': [
			{
				logic: ({ eventData, flags } = {}) =>
					isAdobeUpdatesOn({ flags }) &&
					eventData?.validate === false &&
					eventData?.trackingId !== 'INVENTORY_SEARCH',
				payload: ({ eventData = {} } = {}) => {
					const { invalidFields = {} } = eventData;
					// Every mandatory field's message, pipe-delimited — see
					// buildErrorMessage.
					const message = buildErrorMessage(invalidFields);

					if (!message) {
						return undefined;
					}

					return {
						type: 'CustomTagEvent',
						eventName: 'error',
						eventAction: 'display',
						attributes: {
							message,
							type: 'form validation'
						}
					};
				}
			}
		],
		'ddc.message': [
			{
				logic: {
					taggingType: 'stellantis'
				},
				payload: ({
					eventData: {
						// ignore
						element: _element,
						taggingTarget: _taggingTarget,
						taggingType: _taggingType,
						// keep
						...eventData
					} = {}
				}) => ({
					...eventData
				})
			}
		],
		specialsEvent: [
			// US2093316 — STILL FIRING with the flag on, deliberately. The granular
			// ws-specials rule (US2046689) covers the rotator's CTA CLICKS, but this
			// event reports slide impressions/positions, for which no WSP 2.0 event
			// is built. See docs/stellantis-wsp2-legacy-teardown.md.
			{
				logic: {
					specialPlacementType: 'tall_horizontal_slide',
					specialType: 'click'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'carousel',
						assetName: '[[eventData.title]]',
						// add 1 to start counting on 1 as the other slideshow
						assetPosition: ({ eventData: { promoIndex } = {} }) =>
							promoIndex + 1
					}
				}
			}
		]
	},
	triggerEvents: {
		// Fires a page view for any place whose pageAlias contains 'INVENTORY'.
		// Attached via `trigger` on events that should also emit a page view
		// (e.g. ddc.inventory.filterSearch). Gated behind the
		// ws-tagging-stellantis-adobe-updates flag — off keeps the old behavior.
		inventoryPageView: {
			logic: ({ flags } = {}) =>
				isAdobeUpdatesOn({ flags }) &&
				(window?.DDC?.dataLayer?.page?.pageInfo?.pageName || '')
					.toUpperCase()
					.includes('INVENTORY'),
			payload: {
				type: 'CustomTagEvent',
				eventName: 'JSpageView',
				eventAction: 'contentView',
				attributes: {
					pageID: '[[window.digitalData.page.pageInfo.pageName]]_virtualPageView'
				}
			},
			// Fire once per page, before the standard filteredSearch payload.
			configuration: {
				count: 1,
				when: 'first'
			}
		},
		// US2093316: legacyHamburgerNavClick and legacyDropdownNavClick used to
		// live here. They existed only to re-emit the legacy navClick payloads
		// alongside the granular linkClick events while the flag was on
		// (TC1029576). Publicis confirmed the new events replace the old, so both
		// are deleted; with the flag off the legacy navClick rules in
		// ddc.link.generic still fire directly, exactly as before.
		// This trigger fires from the CTA rule, i.e. for a phone link that is also
		// a `.btn` (the header "Call" button) or a share/ws-content-cta phone.
		// TC1029576: with the flag ON it now carries the SAME granular
		// click-to-call payload as the ddc.link.generic rule, so such a phone
		// emits CTA/buttonClick + exactly one granular clickToCall — restoring the
		// CTA event that first-match-wins ordering had suppressed, without
		// double-counting the clickToCall. With the flag OFF it keeps emitting the
		// original bare-department payload, so legacy behavior is untouched.
		telephonicURL: {
			logic: {
				'eventData.isTelephonicUrl': true
			},
			payload: {
				type: 'CustomTagEvent',
				eventName: 'clickToCall',
				eventAction: 'pageElementInteraction',
				attributes: ({ eventData = {}, flags } = {}) => {
					if (isAdobeUpdatesOn({ flags })) {
						return getGranularClickToCallAttributes(eventData);
					}

					const { department = '' } = eventData;
					const CALL_DEPARTMENTS = [
						'Sales',
						'Service',
						'Parts',
						'Other'
					];

					const dept =
						department.charAt(0).toUpperCase() +
						department.slice(1).toLowerCase();
					const idx = CALL_DEPARTMENTS.indexOf(dept);

					return {
						clickToCallDepartment:
							idx === -1 ? 'Other' : CALL_DEPARTMENTS[idx]
					};
				}
			}
		}
	}
};
